import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import {
  addFinanceRelation,
  archiveFinanceDocument,
  createFinanceDocument,
  getCompanies,
  getFinanceAudit,
  getFinanceDocument,
  getFinanceDocuments,
  getFinanceFileUploadUrl,
  getFinanceSettings,
  getProjects,
  getWorkQuantities,
  setFinancePayment,
  updateFinanceDocument,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { canAccess, canAccessAny } from '../utils/permissions';
import { formatDateEuropean } from '../utils/dateUtils';
import useResponsive from '../components/responsive/useResponsive';
import ResponsiveDataList from '../components/responsive/ResponsiveDataList';
import ResponsivePageSection from '../components/responsive/ResponsivePageSection';
import api from '../services/api';
import FinanceCreateWizard, {
  TYPES,
  emptyForm,
  emptyFinanceLine,
  hasValidLines,
  stepValidationMessage,
  buildFinancePayload,
  lineTotalCheck,
  isLineTotalAcknowledged,
} from './finance/FinanceCreateWizard';
import FinanceDocumentDetail from './finance/FinanceDocumentDetail';

async function uploadOriginal(file) {
  if (!file) return null;
  const presign = await getFinanceFileUploadUrl({
    originalName: file.name,
    contentType: file.type || 'application/octet-stream',
  });
  await fetch(presign.data.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  return {
    objectKey: presign.data.objectKey,
    originalName: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size || 0,
  };
}

function lastStep(type) {
  return type === 'INVOICE' ? 4 : 2;
}

function FinanceDocuments({ type }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const meta = TYPES.find((item) => item.type === type);
  const canWrite = canAccess(user, meta.write);
  const canPrices = canAccess(user, 'finance.prices.read');
  const canSettings = canAccessAny(user, ['finance.quotes.write', 'finance.invoices.write']);

  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ search: '', projectId: '', companyId: '', status: '', paymentStatus: '' });
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [audit, setAudit] = useState([]);
  const [form, setForm] = useState(emptyForm(type));
  const [error, setError] = useState('');
  const [duplicates, setDuplicates] = useState([]);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [linkSearchType, setLinkSearchType] = useState(type === 'INVOICE' ? 'QUOTE' : 'DELIVERY_NOTE');
  const [linkOptions, setLinkOptions] = useState([]);
  const [workQuantities, setWorkQuantities] = useState([]);
  const [mismatch, setMismatch] = useState(null);

  const isOutgoing = type === 'INVOICE' && form.invoice.direction === 'OUTGOING';

  const load = async () => {
    const params = { type, search: filters.search || undefined, projectId: filters.projectId || undefined, companyId: filters.companyId || undefined };
    if (type === 'QUOTE' && filters.status) params.status = filters.status;
    if (type === 'INVOICE' && filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
    const res = await getFinanceDocuments(params);
    setRows(res.data || []);
  };

  useEffect(() => {
    (async () => {
      const [p, c, s] = await Promise.all([getProjects(), getCompanies(), getFinanceSettings()]);
      setProjects(p.data || []);
      setCompanies(c.data || []);
      setCategories(s.data?.categories || []);
    })();
  }, []);

  useEffect(() => { load().catch(() => setRows([])); }, [type, filters.search, filters.projectId, filters.companyId, filters.status, filters.paymentStatus]);

  const searchRelated = async (query, searchType) => {
    const targetType = searchType || linkSearchType;
    if (!TYPES.find((item) => item.type === targetType) || !canAccess(user, TYPES.find((item) => item.type === targetType).read)) {
      setLinkOptions([]);
      return;
    }
    try {
      const res = await getFinanceDocuments({ type: targetType, search: query || undefined });
      setLinkOptions((res.data || []).filter((row) => row.id !== detail?.id));
    } catch (err) {
      setLinkOptions([]);
    }
  };

  useEffect(() => {
    searchRelated('').catch(() => setLinkOptions([]));
  }, [linkSearchType, type, open, detail]);

  useEffect(() => {
    const projectIds = [...new Set((form.lines || []).map((line) => line.projectId).filter(Boolean))];
    if (!isOutgoing || !projectIds.length) {
      setWorkQuantities([]);
      return;
    }
    Promise.all(projectIds.map((projectId) => getWorkQuantities({ projectId })))
      .then((results) => setWorkQuantities(results.flatMap((res) => res.data || [])))
      .catch(() => setWorkQuantities([]));
  }, [isOutgoing, form.lines]);

  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      getFinanceDocument(openId).then((res) => setDetail(res.data)).catch(() => {});
    }
  }, [searchParams]);

  const openCreate = () => {
    setError('');
    setDuplicates([]);
    setStep(0);
    setForm(emptyForm(type));
    setOpen(true);
  };

  const addLine = (preset = {}) => {
    setForm((prev) => ({
      ...prev,
      lines: [...prev.lines, {
        ...emptyFinanceLine(type, preset.projectId),
        ...preset,
      }],
    }));
  };

  const addWorkQuantityLine = (row) => {
    const sheet = row.costSheet || {};
    const qty = row.quantity ?? sheet.quantity ?? '';
    const unitPrice = sheet.unitPrice ?? '';
    const total = qty !== '' && unitPrice !== '' ? Number(qty) * Number(unitPrice) : '';
    addLine({
      description: sheet.name || sheet.code || t('nav.workQuantities'),
      quantity: qty,
      unit: sheet.unit || 'kom',
      unitPrice,
      totalAmount: total === '' ? '' : String(total),
      projectId: sheet.projectId || row.costSheet?.projectId || '',
    });
  };

  const linesIndex = type === 'INVOICE' ? 3 : 1;

  const tryGoNext = () => {
    const message = stepValidationMessage(form, type, step, t);
    if (message) {
      setError(message);
      return;
    }
    if (step === linesIndex && !isLineTotalAcknowledged(form, type)) {
      const check = lineTotalCheck(form, type);
      if (check.status !== 'ok') {
        setMismatch(check);
        return;
      }
    }
    setError('');
    setStep((s) => s + 1);
  };

  const acknowledgeMismatch = () => {
    setForm((prev) => ({ ...prev, lineTotalAcknowledged: mismatch }));
    setMismatch(null);
    setError('');
    setStep((s) => s + 1);
  };

  const submit = async (confirmDuplicate = false) => {
    setSaving(true);
    setError('');
    try {
      const payload = buildFinancePayload(form, type, { confirmDuplicate, isOutgoing });
      const file = await uploadOriginal(form.attachmentFile);
      if (file) payload.files = [file];
      if (type === 'INVOICE' && !file) {
        setError(t('finance.originalRequired'));
        setSaving(false);
        return;
      }
      await createFinanceDocument(payload);
      setOpen(false);
      await load();
    } catch (err) {
      if (err?.response?.data?.code === 'DUPLICATE') {
        setDuplicates(err.response.data.duplicates || []);
        setError(t('finance.duplicateWarning'));
      } else if (err?.response?.data?.code === 'CONFIRM_UNASSIGNED') {
        setError(t('finance.confirmUnassigned'));
        setForm((prev) => ({ ...prev, confirmUnassigned: true }));
      } else if (err?.response?.data?.code === 'LINES_REQUIRED') {
        setError(t('finance.linesRequiredError'));
      } else {
        setError(err?.response?.data?.error || t('finance.saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id) => {
    const res = await getFinanceDocument(id);
    setDetail(res.data);
    const logs = await getFinanceAudit(id);
    setAudit(logs.data || []);
  };

  const downloadFile = async (docId, fileId, name) => {
    const res = await api.get(`/api/finance/documents/${docId}/files/${fileId}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    { field: 'documentDate', headerName: t('finance.date'), width: 120, valueFormatter: (p) => formatDateEuropean(p.value) },
    { field: 'documentNumber', headerName: t('finance.number'), width: 140 },
    { field: 'counterpartyName', headerName: t('finance.partner'), flex: 1, minWidth: 180 },
    ...(canPrices ? [{ field: 'amount', headerName: t('finance.amount'), width: 120, valueGetter: (p) => p.row.quote?.totalAmount ?? p.row.invoice?.totalAmount ?? '' }] : []),
    { field: 'status', headerName: t('finance.status'), width: 160, valueGetter: (p) => p.row.quote?.status || p.row.invoice?.paymentStatus || '' },
  ];

  return (
    <ResponsivePageSection>
      <Typography variant="h4" sx={{ mb: 2 }}>{t('nav.finance')}</Typography>
      <Tabs
        value={type}
        onChange={(_, value) => navigate(TYPES.find((item) => item.type === value)?.path || '/finance/quotes')}
        sx={{ mb: 2 }}
      >
        {TYPES.filter((item) => canAccess(user, item.read)).map((item) => (
          <Tab key={item.type} value={item.type} label={t(`finance.types.${item.type}`)} />
        ))}
        {canSettings && <Tab value="settings" label={t('finance.settings')} onClick={() => navigate('/finance/settings')} />}
      </Tabs>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <TextField fullWidth size="small" label={t('finance.search')} value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('finance.project')}</InputLabel>
            <Select label={t('finance.project')} value={filters.projectId} onChange={(e) => setFilters((p) => ({ ...p, projectId: e.target.value }))}>
              <MenuItem value="">{t('finance.all')}</MenuItem>
              {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        {canWrite && type !== 'DELIVERY_NOTE' && (
          <Grid item xs={12} md={3}>
            <Button variant="contained" onClick={openCreate}>{t('finance.new')}</Button>
          </Grid>
        )}
      </Grid>

      {isMobile ? (
        <ResponsiveDataList
          rows={rows}
          onRowClick={(row) => openDetail(row.id)}
          getTitle={(row) => row.counterpartyName}
          getSubtitle={(row) => `${formatDateEuropean(row.documentDate)} · ${row.documentNumber || '-'}`}
        />
      ) : (
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          pageSize={10}
          onRowClick={(params) => openDetail(params.row.id)}
          localeText={{ noRowsLabel: t('common.noData') }}
        />
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('finance.new')} — {t(`finance.types.${type}`)}</DialogTitle>
        <DialogContent>
          <FinanceCreateWizard
            t={t}
            type={type}
            user={user}
            form={form}
            setForm={setForm}
            step={step}
            error={error}
            duplicates={duplicates}
            companies={companies}
            projects={projects}
            categories={categories}
            canPrices={canPrices}
            linkSearchType={linkSearchType}
            setLinkSearchType={setLinkSearchType}
            linkOptions={linkOptions}
            searchRelated={searchRelated}
            workQuantities={workQuantities}
            addLine={addLine}
            addWorkQuantityLine={addWorkQuantityLine}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          {step > 0 && <Button onClick={() => { setError(''); setStep((s) => s - 1); }}>{t('finance.back')}</Button>}
          {step < lastStep(type) && (
            <Button variant="contained" onClick={tryGoNext}>{t('finance.next')}</Button>
          )}
          {step === lastStep(type) && (
            <Button variant="contained" disabled={saving || !hasValidLines(form, type)} onClick={() => submit(Boolean(duplicates.length))}>
              {duplicates.length ? t('finance.saveAnyway') : t('common.save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(mismatch)} onClose={() => setMismatch(null)}>
        <DialogTitle>
          {mismatch?.status === 'discount' ? t('finance.lineTotalDiscountTitle') : t('finance.lineTotalMismatchTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            {mismatch?.status === 'discount'
              ? t('finance.lineTotalDiscountBody', {
                  lineSum: mismatch.lineSum,
                  docTotal: mismatch.docTotal,
                  percent: mismatch.percent,
                })
              : t('finance.lineTotalMismatchBody', {
                  lineSum: mismatch?.lineSum,
                  docTotal: mismatch?.docTotal,
                })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMismatch(null)}>{t('finance.fixLines')}</Button>
          <Button variant="contained" onClick={acknowledgeMismatch}>
            {mismatch?.status === 'discount' ? t('finance.confirmDiscount') : t('finance.continueMismatch')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} fullWidth maxWidth="lg">
        <DialogTitle>{detail?.counterpartyName} {detail?.documentNumber ? `· ${detail.documentNumber}` : ''}</DialogTitle>
        <DialogContent>
          {detail && (
            <FinanceDocumentDetail
              t={t}
              type={type}
              user={user}
              canWrite={canWrite}
              canPrices={canPrices}
              detail={detail}
              audit={audit}
              projects={projects}
              linkSearchType={linkSearchType}
              setLinkSearchType={setLinkSearchType}
              linkOptions={linkOptions}
              searchRelated={searchRelated}
              onLink={async (otherId) => {
                await addFinanceRelation(detail.id, otherId);
                await openDetail(detail.id);
              }}
              onDownload={downloadFile}
              onPaid={async () => {
                await setFinancePayment(detail.id, { paid: detail.invoice?.paymentStatus !== 'PAID' });
                await openDetail(detail.id);
                load();
              }}
              onStatus={async (status) => {
                await updateFinanceDocument(detail.id, { quote: { status } });
                await openDetail(detail.id);
                load();
              }}
              onArchive={async () => {
                await archiveFinanceDocument(detail.id);
                setDetail(null);
                load();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </ResponsivePageSection>
  );
}

export function FinanceHome() {
  const { user } = useAuth();
  if (canAccess(user, 'finance.quotes.read')) return <Navigate to="/finance/quotes" replace />;
  if (canAccess(user, 'finance.deliveries.read')) return <Navigate to="/finance/deliveries" replace />;
  if (canAccess(user, 'finance.invoices.read')) return <Navigate to="/finance/invoices" replace />;
  return <Navigate to="/" replace />;
}

export default FinanceDocuments;
